/**
 * MuiPaymentsList - Optimized Payments List Page
 *
 * OPTIMIZATION: Phase 5 - Server-side pagination implementation
 *
 * Changes made:
 * - Replaced getPayments() with usePaginatedData(getPaymentsPaginated) hook
 * - Added debounced search (300ms) - server-side text search across entity_name, series, bankname
 * - Bound filter (Inbound/Outbound) now triggers server-side filter
 * - Type filter (CASH/CARD/BANK/CHECK) now triggers server-side filter
 * - Analytics data fetched separately via fetchPaymentAnalytics() to avoid computing from paginated data
 * - Server-side pagination with limit/offset
 * - Server-side sorting by date DESC (default)
 *
 * Benefits:
 * - Only loads visible page data (e.g., 25 records instead of 1000+)
 * - Filter changes trigger efficient server queries
 * - Search queries happen on server, reducing client memory usage
 * - Faster initial load and navigation between pages
 */
import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import moment from "moment"
import {
  Box,
  Paper,
  CircularProgress,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  Grid,
} from "@mui/material"
import { Link as RouterLink } from "react-router-dom"
import SearchIcon from "@mui/icons-material/Search"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import RefreshIcon from "@mui/icons-material/Refresh"
import PaymentsIcon from "@mui/icons-material/Payments"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import TrendingDownIcon from "@mui/icons-material/TrendingDown"
import AccountBalanceIcon from "@mui/icons-material/AccountBalance"
import HomeIcon from "@mui/icons-material/Home"
import BarChartIcon from "@mui/icons-material/BarChart"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts"
import { getPaymentsPaginated, fetchPaymentAnalytics } from "../../helpers/api_helper"
import { usePaginatedData } from "../../hooks/usePaginatedData"

// Format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return "€0.00"
  return new Intl.NumberFormat("el-GR", { style: "currency", currency: "EUR" }).format(amount)
}

const MuiPaymentsList = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  document.title = t("Payments") + " | FlexFlow ERP"

  // Analytics state (fetched separately from paginated data)
  const [analytics, setAnalytics] = useState({
    total: 0,
    inboundTotal: 0,
    outboundTotal: 0,
    inboundCount: 0,
    outboundCount: 0,
    net: 0,
    methodChartData: [],
    paymentTypes: []
  })
  const [analyticsLoading, setAnalyticsLoading] = useState(true)
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false)

  // Filter state (will be passed to server)
  const [boundFilter, setBoundFilter] = useState("all")
  const [typeFilter, setTypeFilter] = useState("all")

  // ============================================================================
  // SERVER-SIDE PAGINATION via usePaginatedData hook
  // ============================================================================
  const {
    data: payments,
    total,
    loading,
    error,
    pagination,
    searchTerm,
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
    handleFilterChange,
    refresh
  } = usePaginatedData(getPaymentsPaginated, {
    initialPageSize: 25,
    initialFilters: { bound: "all", type: "all" },
    debounceMs: 300,
    autoFetch: true
  })

  // ============================================================================
  // FETCH ANALYTICS SEPARATELY (lightweight query)
  // ============================================================================
  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true)
    try {
      const data = await fetchPaymentAnalytics()
      setAnalytics(data)
    } catch (err) {
      console.error("Error loading analytics:", err)
    } finally {
      setAnalyticsLoading(false)
    }
  }, [])

  // Load analytics on mount and when location changes
  useEffect(() => {
    loadAnalytics()
  }, [location.key, loadAnalytics])

  // ============================================================================
  // FILTER HANDLERS - trigger server-side filter
  // ============================================================================
  const handleBoundFilterChange = useCallback((newBound) => {
    setBoundFilter(newBound)
    handleFilterChange({ bound: newBound })
  }, [handleFilterChange])

  const handleTypeFilterChange = useCallback((newType) => {
    setTypeFilter(newType)
    handleFilterChange({ type: newType })
  }, [handleFilterChange])

  // ============================================================================
  // CHART DATA (derived from analytics)
  // ============================================================================
  const boundChartData = useMemo(() => {
    return [
      { name: t("Inbound"), value: analytics.inboundTotal, color: "#10b981" },
      { name: t("Outbound"), value: analytics.outboundTotal, color: "#ef4444" },
    ]
  }, [analytics, t])

  const handleRowClick = (payment) => {
    navigate(`/payment-overview/${payment.id}`)
  }

  const handleRefresh = useCallback(() => {
    refresh()
    loadAnalytics()
  }, [refresh, loadAnalytics])

  return (
    <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1,
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 1,
          border: "1px solid #e5e7eb",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: "#6b7280" }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator="/" sx={{ "& .MuiBreadcrumbs-separator": { mx: 0.5, color: "#9ca3af" } }}>
            <Link
              component={RouterLink}
              to="/"
              sx={{ display: "flex", alignItems: "center", color: "#6b7280", textDecoration: "none", fontSize: "0.813rem" }}
            >
              <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
            </Link>
            <Typography sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#111827", display: "flex", alignItems: "center" }}>
              <PaymentsIcon sx={{ fontSize: 16, mr: 0.5, color: "#10b981" }} />
              {t("Payments")}
            </Typography>
          </Breadcrumbs>
        </Stack>

        <IconButton size="small" onClick={handleRefresh} sx={{ color: "#6b7280", width: 32, height: 32 }}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Paper>

      {/* Expandable Analytics Panel */}
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          borderRadius: 1,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        <Box
          onClick={() => setAnalyticsExpanded(!analyticsExpanded)}
          sx={{
            px: 2,
            py: 0.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            bgcolor: analyticsExpanded ? "#f9fafb" : "#fff",
            "&:hover": { bgcolor: "#f9fafb" },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <BarChartIcon sx={{ fontSize: 18, color: "#6b7280" }} />
            <Typography sx={{ fontSize: "0.813rem", fontWeight: 500, color: "#374151" }}>
              {t("Analytics")}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", ml: 2 }}>
              {analytics.total} {t("payments")} · {t("Net")}: {formatCurrency(analytics.net)}
            </Typography>
          </Stack>
          <IconButton size="small" sx={{ p: 0.25 }}>
            {analyticsExpanded ? (
              <ExpandLessIcon sx={{ fontSize: 20, color: "#6b7280" }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 20, color: "#6b7280" }} />
            )}
          </IconButton>
        </Box>
        <Collapse in={analyticsExpanded}>
          <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb" }}>
            {/* Stats Summary */}
            <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PaymentsIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                  {t("Total")}: <strong style={{ color: "#111827" }}>{analytics.total}</strong>
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <TrendingUpIcon sx={{ fontSize: 16, color: "#10b981" }} />
                <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                  {t("Inbound")}: <strong style={{ color: "#10b981" }}>{formatCurrency(analytics.inboundTotal)}</strong>
                  <span style={{ color: "#9ca3af", marginLeft: 4 }}>({analytics.inboundCount})</span>
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <TrendingDownIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                  {t("Outbound")}: <strong style={{ color: "#ef4444" }}>{formatCurrency(analytics.outboundTotal)}</strong>
                  <span style={{ color: "#9ca3af", marginLeft: 4 }}>({analytics.outboundCount})</span>
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: "auto" }}>
                <AccountBalanceIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
                <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                  {t("Net")}: <strong style={{ color: analytics.net >= 0 ? "#10b981" : "#ef4444" }}>{formatCurrency(analytics.net)}</strong>
                </Typography>
              </Stack>
            </Stack>

            {/* Charts */}
            <Grid container spacing={2}>
              {/* Payments by Method Chart */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
                  {t("Payments by Method")}
                </Typography>
                <Box sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics.methodChartData} layout="vertical" margin={{ top: 5, right: 20, left: 60, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "#6b7280" }} width={55} />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 4, border: "1px solid #e5e7eb" }}
                        formatter={(value, name) => {
                          if (name === "count") return [value, t("Count")]
                          return [formatCurrency(value), t("Amount")]
                        }}
                      />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                        {analytics.methodChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>

              {/* Inbound vs Outbound Chart */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
                  {t("Inbound vs Outbound")}
                </Typography>
                <Box sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={boundChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 4, border: "1px solid #e5e7eb" }}
                        formatter={(value) => [formatCurrency(value), t("Amount")]}
                      />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {boundChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Search and Filters */}
      <Paper elevation={0} sx={{ px: 2, py: 1, mb: 1.5, borderRadius: 1, border: "1px solid #e5e7eb" }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder={t("Search by entity, ref, bank...")}
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{
              width: 240,
              "& .MuiOutlinedInput-root": {
                height: 32,
                fontSize: "0.813rem",
                bgcolor: "#f9fafb",
                "& fieldset": { borderColor: "#e5e7eb" },
                "&:hover fieldset": { borderColor: "#d1d5db" },
                "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={0.5}>
            {["all", "Inbound", "Outbound"].map((bound) => (
              <Chip
                key={bound}
                label={bound === "all" ? t("All") : bound === "Inbound" ? t("Inbound") : t("Outbound")}
                size="small"
                onClick={() => handleBoundFilterChange(bound)}
                sx={{
                  height: 26,
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  bgcolor: boundFilter === bound ? (bound === "Inbound" ? "#10b981" : bound === "Outbound" ? "#ef4444" : "#3b82f6") : "#f3f4f6",
                  color: boundFilter === bound ? "#fff" : "#374151",
                  "&:hover": { bgcolor: boundFilter === bound ? (bound === "Inbound" ? "#059669" : bound === "Outbound" ? "#dc2626" : "#2563eb") : "#e5e7eb" },
                }}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <Chip
              label={t("All Methods")}
              size="small"
              onClick={() => handleTypeFilterChange("all")}
              sx={{
                height: 26,
                fontSize: "0.7rem",
                fontWeight: 500,
                bgcolor: typeFilter === "all" ? "#8b5cf6" : "#f3f4f6",
                color: typeFilter === "all" ? "#fff" : "#374151",
                "&:hover": { bgcolor: typeFilter === "all" ? "#7c3aed" : "#e5e7eb" },
              }}
            />
            {analytics.paymentTypes.slice(0, 4).map((type) => (
              <Chip
                key={type}
                label={type}
                size="small"
                onClick={() => handleTypeFilterChange(type)}
                sx={{
                  height: 26,
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  bgcolor: typeFilter === type ? "#8b5cf6" : "#f3f4f6",
                  color: typeFilter === type ? "#fff" : "#374151",
                  "&:hover": { bgcolor: typeFilter === type ? "#7c3aed" : "#e5e7eb" },
                }}
              />
            ))}
          </Stack>
          <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", ml: "auto" }}>
            {total} {t("results")}
          </Typography>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 1, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f9fafb" }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25, width: 60 }}>
                      ID
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Date")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Entity")}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Type")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Method")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Ref")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Bank")}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Amount")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.length > 0 ? (
                    payments.map((payment) => (
                      <TableRow
                        key={payment.id}
                        hover
                        onClick={() => handleRowClick(payment)}
                        sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f9fafb" } }}
                      >
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                            {payment.id}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography sx={{ fontSize: "0.813rem", color: "#374151" }}>
                            {payment.date ? moment(payment.date).format("DD/MM/YY") : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography sx={{ fontSize: "0.813rem", fontWeight: 500, color: "#111827" }}>
                            {payment.entity_name || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.25 }}>
                          <Chip
                            size="small"
                            label={payment.bound === "Inbound" ? t("In") : t("Out")}
                            sx={{
                              height: 22,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              bgcolor: payment.bound === "Inbound" ? "#dcfce7" : "#fee2e2",
                              color: payment.bound === "Inbound" ? "#166534" : "#991b1b",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Chip
                            size="small"
                            label={payment.type || "-"}
                            sx={{
                              height: 22,
                              fontSize: "0.7rem",
                              fontWeight: 500,
                              bgcolor: "#f3f4f6",
                              color: "#374151",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography sx={{ fontSize: "0.813rem", fontFamily: "monospace", color: "#6b7280" }}>
                            {payment.series || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                            {payment.bankname || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.25 }}>
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: 700,
                              color: payment.bound === "Inbound" ? "#16a34a" : "#dc2626",
                            }}
                          >
                            {payment.bound === "Inbound" ? "+" : "-"}{formatCurrency(Number(payment.ammount) || 0).replace("€", "")}€
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <PaymentsIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
                        <Typography sx={{ fontSize: "0.875rem", color: "#9ca3af" }}>
                          {searchTerm || boundFilter !== "all" || typeFilter !== "all" ? t("No payments found matching your criteria") : t("No payments found")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={pagination.page}
              onPageChange={(e, newPage) => handlePageChange(newPage)}
              rowsPerPage={pagination.pageSize}
              onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={t("Rows")}
              sx={{
                borderTop: "1px solid #e5e7eb",
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: "0.75rem",
                },
                "& .MuiTablePagination-select": {
                  fontSize: "0.75rem",
                },
              }}
            />
          </>
        )}
      </Paper>
    </Box>
  )
}

export default MuiPaymentsList
